import { Component } from '@angular/core';
import {
  AarResponse,
  Anlegg,
  AnleggAar,
  AnleggMaaned,
  ChartType,
  DagMonthResponse,
  DagResponse,
  FremtidigeAvtaler,
  MaanedResponse
} from '../../services/forbruk/forbruk.types';
import { ForbrukService } from '../../services/forbruk/forbruk.service';
import {
  ModalController,
  Platform,
  RefresherCustomEvent
} from '@ionic/angular';
import { Observable, Subscription, of, forkJoin } from 'rxjs';
import { registerLocaleData } from '@angular/common';
import localeNb from '@angular/common/locales/nb';
import { DownloadForbrukPopupPage } from './download-forbruk-popup/download-forbruk-popup.page';
import { KeycloakService } from 'keycloak-angular';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import { DataService } from '../../services/datatransfer/DataService';
import { InformationService } from 'src/app/services/notify/information.service';
import { InformationMessage } from 'src/app/services/notify/information.types';
import {
  addDays,
  differenceInCalendarYears,
  differenceInDays,
  differenceInMonths,
  isSameMonth
} from 'date-fns';
import { getDayDictKey, getHourDictKey } from './forbruk-utils';
import { Avtaler, AvtaleResponse } from 'src/app/services/avtale/avtale.types';
import { AvtaleService } from 'src/app/services/avtale/avtale.service';
import { FeatureToggle } from 'src/app/services/feature-toggles/toggles.types';
import { FeatureTogglesService } from 'src/app/services/feature-toggles/feature-toggles.service';
import { BrukerService } from 'src/app/services/bruker/bruker.service';
import { SanityService } from 'src/app/services/sanity/sanity.service';
import {
  AllGraphDescriptions,
  GraphDescription
} from 'src/app/services/sanity/sanity.types';
import { StromstotteService } from 'src/app/services/stromstotte/stromstotte.service';
import { analyseRoute } from 'src/app/routes';
import { Router } from '@angular/router';

registerLocaleData(localeNb, 'nb');

@Component({
  selector: 'app-forbrukpage',
  templateUrl: './forbruk.page.html',
  styleUrls: ['./forbruk.page.scss']
})
export class ForbrukPage {
  _chartTypes: ChartType;
  anleggList: Anlegg[];
  valgtAnlegg: Anlegg;
  _showAnleggsliste: boolean;
  mineAvtaler: Avtaler[];
  valgtAvtale: Avtaler;
  fremtidigeAvtaler: FremtidigeAvtaler[];
  valgtAnleggHasData: boolean;

  today: Date;
  startDate: Date;
  twoYearsAgo: Date;
  maxDate: Date;
  minDate: Date;

  availableYearsForbruk: number[];
  selectedYearsForbrukMonth: number[];
  selectedDateMonth: Date;
  selectedDateDay: Date;
  selectedDateHour: Date;

  _yearResponse: Observable<AarResponse>;
  _monthResponse: Observable<MaanedResponse>;
  _dayResponse: Observable<DagMonthResponse>;
  _hourResponse: Observable<DagResponse>;
  _avtaleResponse: Observable<AvtaleResponse>;
  toggleResponse: Observable<FeatureToggle[]>;
  hasFetchingError: boolean;

  valgtAarAnlegg: AnleggAar;
  valgtMaanedAnlegg: AnleggMaaned;
  valgtDagAnlegg: Anlegg;
  valgtTimeAnlegg: Anlegg;

  yearDictionary: Map<number, AnleggAar[]>;
  monthDictionary: Map<number, AnleggMaaned[]>;
  dayDictionary: Map<string, Anlegg[]>;
  hourDictionary: Map<string, Anlegg[]>;
  _foretakMap: Map<number, string> = new Map<number, string>();

  graphDescriptions: AllGraphDescriptions;
  activeGraphDescription: GraphDescription;

  modalDownloadPage: any;
  showNoAnlegg = false;
  changingAnleggInProgress = false;
  subscriptionInit = false;
  infoMessages: InformationMessage[];
  isBrowser: boolean;
  isStromstotteToggled: boolean;

  kunde_id: string;

  initSubscription: Subscription;
  infoMsgSubscription: Subscription;
  kundebytteSubscription: Subscription = new Subscription();
  getKundeSubscription: Subscription;
  yearSubscription: Subscription;
  monthSubscription: Subscription;
  daySubscription: Subscription;
  hourSubscription: Subscription;
  chartSubscription: Subscription;

  constructor(
    private _forbrukService: ForbrukService,
    private dataService: DataService,
    private analyticsService: AnalyticsService,
    private sanityService: SanityService,
    private _avtaleService: AvtaleService,
    private platform: Platform,
    public keycloak: KeycloakService,
    public modalCtrl: ModalController,
    public infoService: InformationService,
    private toggleService: FeatureTogglesService,
    private _brukerService: BrukerService,
    private _stromstotteService: StromstotteService,
    private router: Router
  ) {
    this.today = new Date();
    this.startDate = addDays(this.today, -1);
    this.maxDate = this.startDate;
    this.twoYearsAgo = new Date(
      this.startDate.getFullYear() - 2,
      0,
      1,
      0,
      0,
      0
    );
    this.minDate = this.twoYearsAgo;

    this.selectedDateMonth = this.startDate;
    this.selectedDateDay = this.startDate;
    this.selectedDateHour = this.startDate;

    this.dayDictionary = new Map<string, Anlegg[]>();
    this.hourDictionary = new Map<string, Anlegg[]>();
    this.monthDictionary = new Map<number, AnleggMaaned[]>();
    this.yearDictionary = new Map<number, AnleggAar[]>();

    this.toggleResponse = this.toggleService.getActiveFeatureToggles();

    this.hasFetchingError = false;
    this.valgtAnleggHasData = true;
  }

  ionViewWillEnter() {
    this.fetchData();
    this.kundebytteSubscription = this.dataService
      .getKundebytte()
      ?.subscribe((x) => {
        if (x !== this.kunde_id) {
          this.dayDictionary = new Map<string, Anlegg[]>();
          this.hourDictionary = new Map<string, Anlegg[]>();
          this.monthDictionary = new Map<number, AnleggMaaned[]>();
          this.yearDictionary = new Map<number, AnleggAar[]>();

          this.fetchStromstotte();
          this.fetchData();
          this.updateChart(true);
        }
      });
  }

  fetchData() {
    this.subscriptionInit = false;
    this.isBrowser =
      !this.platform.is('capacitor') && this.platform.width() >= 1200;
    this.keycloak.loadUserProfile(true);
    this.sanityService.getGraphDescriptions().then((res) => {
      this.graphDescriptions = res;
    });
    // Hent avtale og forbruk
    this.initSubscription = forkJoin({
      avtaleRes: this._avtaleService.getAvtaler(this.selectedDateDay),
      forbrukRes: this._forbrukService.getDagForbrukSiste(true),
      toggleRes: this.toggleResponse
    })?.subscribe({
      next: ({ avtaleRes, forbrukRes, toggleRes }) => {
        if (avtaleRes?.avtaler?.length === 0) {
          this.showNoAnlegg = true;
        }
        this.isStromstotteToggled =
          this.getIsStromstotteToggled(toggleRes) ?? false;

        if (!forbrukRes) {
          this.hasFetchingError = true;
        } else {
          // Månedsforbruk
          this.anleggList = forbrukRes.anlegg.sort((a, b) => 

            b.priser.forbrukKWh - a.priser.forbrukKWh 
          )
          const anleggsListe = forbrukRes.anlegg;
          if (anleggsListe.length > 0) {
            this.valgtAnlegg = anleggsListe[0];
            this.kunde_id = this.valgtAvtale?.kunde_id;
            this.valgtDagAnlegg = this.valgtAnlegg;
            this.minDate = new Date(
              this.startDate.getFullYear() - 2,
              0,
              1,
              0,
              0,
              0
            );

            this.valgtAnleggHasData =
              this.valgtAnlegg && this.valgtAnlegg.verdiList.length > 0;

            this.maxDate =
              this.valgtAnlegg?.verdiList?.length > 0
                ? new Date(
                    this.valgtAnlegg.verdiList.reduce((acc, curr) =>
                      new Date(acc.dato) > new Date(curr.dato) ? acc : curr
                    )?.dato
                  )
                : this.startDate;
            // Ikke sikkert vi har fått inn gårsdagen enda
            this.selectedDateDay = this.maxDate;

            // Hent strømstøttedata til de andre komponentene
            this._stromstotteService.fetchData(
              this.valgtAnlegg?.objectid,
              this.selectedDateDay
            );

            const dayKey = getDayDictKey(this.selectedDateDay);
            this.dayDictionary.set(dayKey, forbrukRes.anlegg);

            const firstDayWithData = new Date(this.valgtAnlegg.fradato);
            if (firstDayWithData > this.minDate) {
              // Vi har ikke data for hele siste to år
              this.minDate = firstDayWithData;
            }
            const lastDayWithData = addDays(
              new Date(this.valgtAnlegg.tildato),
              -1
            );
            if (lastDayWithData < this.maxDate) {
              // Vi har ikke data frem til dagens dato
              this.maxDate = lastDayWithData;
            }

            this.availableYearsForbruk = [
              ...Array(1 + differenceInCalendarYears(this.today, this.minDate))
            ]
              .map((_, i) => this.today.getFullYear() - i)
              .reverse();
            this.selectedYearsForbrukMonth =
              this.availableYearsForbruk.length < 3
                ? this.availableYearsForbruk
                : this.availableYearsForbruk.slice(-2);

            if (!this.valgtAnlegg.timeavlest) {
              this._chartTypes.Dag.visible = false;
              this._chartTypes.Time.visible = false;
            }
          }

          // Vis dropdown med anlegg om vi har flere
          if (anleggsListe.length > 1) {
            this._showAnleggsliste = true;
          } else {
            this._showAnleggsliste = false;
          }

          // Ingen anlegg å vise, sjekker fremtidige avtaler
          if (anleggsListe.length == 0) {
            this.showNoAnlegg = true;
          }
          this.fremtidigeAvtaler = forbrukRes.fremtidigeAvtaler;

          if (!this.showNoAnlegg) {
            this.updateChart();
          }
          // Avtale når vi vet valgt anlegg
          // Brukes av nøkkeltall
          this.mineAvtaler = avtaleRes.avtaler;
          this.valgtAvtale = this.mineAvtaler.find(
            (x) => x.anleggsadresse === this.valgtAnlegg?.adresse
          );
        }
      },
      error: (err) => {
        this.hasFetchingError = true;
      }
    });

    this.infoService.getInfoMessages().subscribe({
      next: (res) => {
        this.infoMessages = res.filter((x) => x.aktivert) || [];
      }
    });
    this.getForbruksType();

    this.chartSubscription = this._forbrukService
      .getChartType()
      .subscribe((chartTypes) => {
        this._chartTypes = chartTypes;
        if (chartTypes.Time.active || chartTypes.Dag.active) {
          this.fetchStromstotte();
        }
        this.updateChart();
      });
  }

  ionViewWillLeave() {
    this.daySubscription?.unsubscribe();
    this.hourSubscription?.unsubscribe();
    this.monthSubscription?.unsubscribe();
    this.yearSubscription?.unsubscribe();
    this.initSubscription?.unsubscribe();
    this.infoMsgSubscription?.unsubscribe();
    this.kundebytteSubscription?.unsubscribe();
    this.getKundeSubscription?.unsubscribe();
    this.chartSubscription?.unsubscribe();
  }

  getForbruksType() {
    this.getKundeSubscription = this._brukerService
      .getKunde()
      ?.subscribe((kunde) => {
        if (kunde.kunde.foretaksnr == null) {
          kunde.kunde.anleggsListe.forEach((anlegg) => {
            this._foretakMap.set(anlegg.lev_punkt_id, anlegg.forbrukstype);
          });
        }
      });
  }

  selectYearForbrukMonth(year: number) {
    if (this.selectedYearsForbrukMonth.includes(year)) {
      this.selectedYearsForbrukMonth = this.selectedYearsForbrukMonth.filter(
        (x) => x !== year
      );
    } else {
      const updatedList = [...this.selectedYearsForbrukMonth, year];
      updatedList.sort();
      this.selectedYearsForbrukMonth = updatedList;
    }
  }

  getIsStromstotteToggled(toggles: FeatureToggle[]) {
    const myToggle = toggles.find((x) => x.navn === 'stromstotte_time');
    return myToggle ? myToggle.toggleActive === 'NOW' : false;
  }

  updateChart(forceFetch?: boolean) {
    this.hasFetchingError = false;
    this.valgtAnleggHasData = true;

    // Hent tekst under graf og cache
    if (!this.graphDescriptions || forceFetch) {
      this.sanityService.getGraphDescriptions().then((res) => {
        this.graphDescriptions = res;
        if (this._chartTypes?.Dag.active) {
          this.activeGraphDescription = this.graphDescriptions.app_forbruk_day;
        }
        if (this._chartTypes?.Time.active) {
          this.activeGraphDescription = this.graphDescriptions.app_forbruk_hour;
        }
        if (this._chartTypes?.Maaned.active) {
          this.activeGraphDescription =
            this.graphDescriptions.app_forbruk_month;
        }
        if (this._chartTypes?.Aar.active) {
          this.activeGraphDescription = this.graphDescriptions.app_forbruk_year;
        }
      });
    }

    if (forceFetch) {
      // Hent varslinger på nytt
      this.infoService.getInfoMessages(true).subscribe({
        next: (res) => {
          this.infoMessages = res.filter((x) => x.aktivert) || [];
        }
      });
    }

    // Year chart
    if (this._chartTypes?.Aar.active) {
      this._yearResponse = this._forbrukService.getForbrukAar(
        true,
        this.minDate.getFullYear()
      );
      const yearData = this.yearDictionary.get(0);
      if (!yearData || yearData.length === 0 || forceFetch) {
        this.valgtAarAnlegg = undefined; // Vis lasteanimasjon
        this.yearSubscription = this._yearResponse?.subscribe({
          next: (res) => {
            this.yearDictionary.set(0, res.anlegg);
            this.valgtAarAnlegg = res.anlegg.find(
              (x) => x.objectid === this.valgtAnlegg.objectid
            );
            this.valgtAnleggHasData =
              this.valgtAarAnlegg &&
              this.valgtAarAnlegg.aarVerdiList.length > 0;
          },
          error: (err) => {
            this.hasFetchingError = true;
          }
        });
      } else {
        this.valgtAarAnlegg = yearData.find(
          (x) => x.objectid === this.valgtAnlegg.objectid
        );
        this.valgtAnleggHasData =
          this.valgtAarAnlegg && this.valgtAarAnlegg.aarVerdiList.length > 0;
      }
      this.activeGraphDescription = this.graphDescriptions?.app_forbruk_year;
    }
    // Month chart
    if (this._chartTypes?.Maaned.active) {
      this._monthResponse = this._forbrukService.getMaanedsforbruk(true);
      const monthData = this.monthDictionary.get(0);
      if (!monthData || monthData.length === 0 || forceFetch) {
        this.valgtMaanedAnlegg = undefined; // Vis lasteanimasjon
        this.monthSubscription = this._monthResponse?.subscribe({
          next: (res) => {
            this.monthDictionary.set(0, res.anlegg);
            this.valgtMaanedAnlegg = res.anlegg.find(
              (x) => x.objectid === this.valgtAnlegg.objectid
            );
            this.valgtAnleggHasData =
              this.valgtMaanedAnlegg &&
              this.valgtMaanedAnlegg.maanedVerdiList.length > 0;
          },
          error: (err) => {
            this.hasFetchingError = true;
          }
        });
      } else {
        this.valgtMaanedAnlegg = monthData.find(
          (x) => x.objectid === this.valgtAnlegg.objectid
        );
        this.valgtAnleggHasData =
          this.valgtMaanedAnlegg &&
          this.valgtMaanedAnlegg.maanedVerdiList.length > 0;
      }

      this.activeGraphDescription = this.graphDescriptions?.app_forbruk_month;
    }
    // Day chart
    if (this._chartTypes?.Dag.active) {
      this._dayResponse = this._forbrukService.getDagforbruk(
        this.selectedDateDay,
        true
      );

      this._avtaleResponse = this._avtaleService.getAvtaler(
        this.selectedDateDay
      );
      const dayKey = getDayDictKey(this.selectedDateDay);
      const dayData = this.dayDictionary.get(dayKey);

      if (!dayData || dayData.length === 0 || forceFetch) {
        this.valgtDagAnlegg = undefined; // Vis lasteanimasjon
        this.daySubscription = forkJoin({
          dayRes: this._dayResponse,
          toggleRes: forceFetch ? this.toggleResponse : of(null),
          avtaleRes: forceFetch ? this._avtaleResponse : of(null)
        })?.subscribe({
          next: ({ dayRes, toggleRes, avtaleRes }) => {
            if (toggleRes) {
              this.isStromstotteToggled =
                this.getIsStromstotteToggled(toggleRes) ?? false;
            }

            this.dayDictionary.set(dayKey, dayRes.anlegg);
            this.valgtDagAnlegg = this.valgtAnlegg
              ? dayRes.anlegg.find(
                  (x) => x.objectid === this.valgtAnlegg.objectid
                )
              : dayRes.anlegg[0];
            this.valgtAnleggHasData =
              this.valgtDagAnlegg && this.valgtDagAnlegg.verdiList.length > 0;
            if (avtaleRes) {
              this.mineAvtaler = avtaleRes.avtaler;
              this.valgtAvtale = this.mineAvtaler.find(
                (x) => x.anleggsadresse === this.valgtAnlegg?.adresse
              );
            }
          },
          error: (err) => {
            this.hasFetchingError = true;
          }
        });
      } else {
        this.valgtDagAnlegg = dayData.find(
          (x) => x.objectid === this.valgtAnlegg.objectid
        );
        this.valgtAnleggHasData =
          this.valgtDagAnlegg && this.valgtDagAnlegg.verdiList.length > 0;
      }

      this.activeGraphDescription = this.graphDescriptions?.app_forbruk_day;
    }
    // Hour chart
    if (this._chartTypes?.Time.active) {
      this._hourResponse = this._forbrukService.getForbrukTimeDag(
        this.selectedDateHour,
        true
      );
      const hourKey = getHourDictKey(this.selectedDateHour);
      const hourData = this.hourDictionary.get(hourKey);
      if (!hourData || hourData.length === 0 || forceFetch) {
        this.valgtTimeAnlegg = undefined; // Vis lasteanimasjon
        this.hourSubscription = this._hourResponse?.subscribe({
          next: (res) => {
            this.hourDictionary.set(hourKey, res.anlegg);
            this.valgtTimeAnlegg = res.anlegg.find(
              (x) => x?.objectid === this.valgtAnlegg?.objectid
            );

            this.valgtAnleggHasData =
              this.valgtTimeAnlegg && this.valgtTimeAnlegg.verdiList.length > 0;

            // Make sure that the last day has data, if not force the view to go back to the previous day
            if (this.valgtTimeAnlegg) {
              const verdiList = this.valgtTimeAnlegg.verdiList;
              if (verdiList && verdiList.every((v) => v.verdi === 0)) {
                this.selectedDateHour = addDays(this.selectedDateHour, -1);
                this.updateChart();
              }
            }
          },
          error: (err) => {
            this.hasFetchingError = true;
          }
        });
      } else {
        this.valgtTimeAnlegg = hourData.find(
          (x) => x.objectid === this.valgtAnlegg.objectid
        );
        this.valgtAnleggHasData =
          this.valgtTimeAnlegg && this.valgtTimeAnlegg.verdiList.length > 0;
      }

      this.activeGraphDescription = this.graphDescriptions?.app_forbruk_hour;
    }
  }

  setAnlegg(anlegg: Anlegg) {
    this.valgtAnlegg = anlegg;
    this.fetchStromstotte();
    this.changeAnlegg();
  }

  changeAnlegg() {
    this.hasFetchingError = false;

    this.changingAnleggInProgress = true;

    this.valgtAnleggHasData =
      this.valgtAnlegg && this.valgtAnlegg.verdiList.length > 0;

    this.maxDate =
      this.valgtAnlegg.verdiList?.length > 0
        ? new Date(
            this.valgtAnlegg.verdiList?.reduce((acc, curr) =>
              new Date(acc.dato) > new Date(curr.dato) ? acc : curr
            )?.dato
          )
        : this.startDate;
    this.minDate = this.twoYearsAgo; // reset
    const firstDayWithData = new Date(this.valgtAnlegg.fradato);
    if (firstDayWithData > this.minDate) {
      // Vi har ikke data for hele siste to år
      this.minDate = firstDayWithData;
    }
    const lastDayWithData = addDays(new Date(this.valgtAnlegg.tildato), -1);
    if (lastDayWithData < this.maxDate) {
      // Vi har ikke data frem til dagens dato
      this.maxDate = lastDayWithData;
    }
    this._chartTypes.Dag.visible = this.valgtAnlegg.timeavlest;
    this._chartTypes.Time.visible = this.valgtAnlegg.timeavlest;

    this.selectedDateMonth = this.maxDate;

    this.selectedDateDay = this.maxDate;

    if (this.selectedDateHour > this.maxDate) {
      this.selectedDateHour = this.maxDate;
    }

    this.valgtAvtale = this.mineAvtaler.find(
      (x) => x.anleggsadresse === this.valgtAnlegg?.adresse
    );

    this.updateChart();

    this.changingAnleggInProgress = false;
  }

  fetchStromstotte() {
    const selectedDate = this._chartTypes.Time.active
      ? this.selectedDateHour
      : this.selectedDateDay;
    this._stromstotteService.fetchData(
      this.valgtAnlegg?.objectid,
      selectedDate
    );
  }

  setSelectedDateDay(date: Date) {
    this.selectedDateDay = date;
    this.updateChart();
    this.fetchStromstotte();
  }

  setSelectedDateHour(date: Date) {
    const oldDate = this.selectedDateHour;
    this.selectedDateHour = date;
    this.updateChart();
    if (!isSameMonth(oldDate, date)) {
      this.fetchStromstotte();
    }
  }

  setSelectedDateMonth(date: Date) {
    this.selectedDateDay = date;
    this.updateChart();
  }

  setSelectedYear(year: number) {
    this.selectedDateMonth = new Date(year, 1, 1);
    this.updateChart();
  }

  getTimeDifferenceInMonths(date: Date): number {
    const today = new Date();
    return differenceInMonths(today, date);
  }

  getTimeDifferenceInDays(date: Date): number {
    const today = new Date();
    return differenceInDays(today, date);
  }

  async doRefresh(myevent: RefresherCustomEvent) {
    this.dayDictionary = new Map<string, Anlegg[]>();
    this.hourDictionary = new Map<string, Anlegg[]>();
    this.monthDictionary = new Map<number, AnleggMaaned[]>();
    this.yearDictionary = new Map<number, AnleggAar[]>();
    this.updateChart(true);
    this.fetchStromstotte();

    myevent.detail.complete();
  }

  async downloadInfo(no: number) {
    this.analyticsService.logEvent_selectContent(
      'forbruk åpne nedlasting til excel'
    );

    const obj = [];
    obj.push(this.anleggList);
    obj.push(this.valgtAnlegg);
    obj.push(no);
    obj.push(this.selectedDateMonth);
    obj.push(this.selectedDateDay);
    obj.push(this.selectedDateHour);
    obj.push(this.maxDate);
    obj.push(this.minDate);
    const prof: any = this.keycloak.getKeycloakInstance().profile;
    if (
      prof.attributes &&
      prof.attributes.kunde_id &&
      prof.attributes.kunde_id.length > 0
    ) {
      obj.push(prof.email);
    } else {
      //Vi kan kanskje bare stoppe funksjonen, eller skjule den?
      obj.push('');
    }
    obj.push(this._showAnleggsliste);

    //obj.push()

    this.modalDownloadPage = await this.modalCtrl.create({
      component: DownloadForbrukPopupPage,
      cssClass: 'download-forbruk-modal-content',
      componentProps: obj,
      backdropDismiss: true
    });
    return await this.modalDownloadPage.present();
  }

  anleggIsInactive(anlegg: Anlegg) {
    return anlegg.tildato ? new Date(anlegg.tildato) < new Date() : false;
  }

  goToAnalyse() {
    this.router.navigate([analyseRoute]);
  }
}
